import { Badge, Btn, StateBadge } from "../../ui";
import { cancelSisterBrowserTasks, prioritizeSisterBrowserTask } from "../../api";
import type { SisterQueueTask } from "../../api";

const CANCELABLE = new Set(["queued", "failed", "needs_review"]);

function taskError(task: SisterQueueTask): string {
  if (task.recovery.needsRecovery) return "Documenti da recuperare da SISTER → Richieste.";
  if (task.error === "too_many_results") return "Troppi risultati: serve verificare la persona.";
  return task.error ?? "—";
}

function RequestRecovery({ task, onCopy }: { task: SisterQueueTask; onCopy: (id: string) => void }) {
  const { idRichiesta, needsRecovery } = task.recovery;
  return (
    <div style={{ marginTop: 6, minWidth: 230, maxWidth: 360 }}>
      {idRichiesta && (
        <div className="row gap-6" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <span className="mono" style={{ overflowWrap: "anywhere" }}>ID richiesta: {idRichiesta}</span>
          <Btn size="sm" variant="ghost" title="Copia l’ID per trovare la richiesta già inoltrata su SISTER" onClick={() => onCopy(idRichiesta)}>Copia ID</Btn>
        </div>
      )}
      {needsRecovery && (
        <details style={{ marginTop: 6, fontSize: 12 }}>
          <summary style={{ cursor: "pointer", color: "var(--text-0)" }}>Come recuperare la visura</summary>
          <ol style={{ margin: "8px 0", paddingLeft: 20, lineHeight: 1.6, color: "var(--text-1)" }}>
            <li>Apri SISTER con il profilo che ha inoltrato la visura e vai in <strong>Richieste</strong>.</li>
            <li>{idRichiesta ? "Trova la richiesta con questo ID." : "L’ID non è disponibile: cerca la richiesta per persona e data."}</li>
            <li>Apri la richiesta e scarica il documento disponibile, senza premere <strong>Inoltra</strong>.</li>
          </ol>
          <p className="muted" style={{ margin: 0 }}>Il download da SISTER non importa automaticamente il documento in Legacy.</p>
        </details>
      )}
    </div>
  );
}

export function SisterQueueRows({ tasks, busy, run, onCopy }: {
  tasks: SisterQueueTask[];
  busy: boolean;
  run: (action: () => Promise<string>) => Promise<void>;
  onCopy: (id: string) => void;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            {["#", "Persona", "Luogo", "Stato / Recupero", "Tent.", "Esito", "Azioni"].map((label) => (
              <th key={label} style={{ textAlign: "left", padding: "8px 12px" }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const requested = task.recovery.alreadyRequested;
            const canArchive = CANCELABLE.has(task.state);
            return (
              <tr key={task.id} style={{ borderTop: "1px solid var(--line)" }}>
                <td className="mono" style={{ padding: "8px 12px" }}>{task.id}</td>
                <td style={{ padding: "8px 12px", fontWeight: 500 }}>
                  {[task.payload.cognome, task.payload.nome].filter(Boolean).join(" ") || task.payload.rawName || "Persona da verificare"}
                  {task.payload.annoNascita ? <span className="mono muted"> ({task.payload.annoNascita})</span> : null}
                </td>
                <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                  {task.payload.comune || "—"}{task.payload.provincia ? ` (${task.payload.provincia})` : ""}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {requested ? <Badge kind={task.recovery.needsRecovery ? "warn" : "info"}>Visura già inoltrata</Badge>
                    : task.state === "skipped" ? <Badge kind="muted">Archiviata / saltata</Badge> : <StateBadge state={task.state} />}
                  {task.state === "skipped" && requested && <div className="muted" style={{ marginTop: 4 }}>Ricerca archiviata</div>}
                  {requested && <RequestRecovery task={task} onCopy={onCopy} />}
                </td>
                <td className="mono" style={{ padding: "8px 12px" }}>{task.attempts}</td>
                <td style={{ padding: "8px 12px", color: "var(--text-2)", maxWidth: 260, overflowWrap: "anywhere" }} title={task.error ?? undefined}>{taskError(task)}</td>
                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                  {task.state === "queued" && !requested && (
                    <Btn size="sm" variant="ghost" disabled={busy} title="Porta in cima alla coda: sarà la prossima ricerca lavorata" onClick={() => void run(async () => {
                      await prioritizeSisterBrowserTask(task.id);
                      return `Ricerca #${task.id} portata in cima alla coda.`;
                    })}>⬆</Btn>
                  )}
                  {canArchive && (
                    <Btn size="sm" variant="ghost" disabled={busy} title={requested
                      ? "Archivia la ricerca già inoltrata. Non annulla la richiesta né l’addebito su SISTER."
                      : task.state === "queued" ? "Rimuovi dalla coda: il necrologio torna selezionabile in anteprima" : "Archivia la ricerca: non sarà riprovata"}
                      onClick={() => void run(async () => {
                        const result = await cancelSisterBrowserTasks([task.id]);
                        return result.removed ? `Ricerca #${task.id} rimossa dalla coda.` : result.archived
                          ? `Ricerca #${task.id} archiviata.` : `Ricerca #${task.id}: nessuna modifica. Lo stato potrebbe essere cambiato; aggiorna la coda.`;
                      })}>{requested || task.state !== "queued" ? "Archivia" : "✕"}</Btn>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
